/**
 * Verificación pura de respuestas por arquetipo.
 *
 * Recibe los datos ya normalizados (no el JSON de autor) y devuelve si la
 * respuesta es correcta junto con un detalle breve del primer fallo. Está
 * separado del DOM para que cada arquetipo se pruebe sin navegador: lo que
 * decide si una respuesta vale es esto, nunca la vista.
 */

export interface CheckResult {
  correct: boolean;
  /** Detalle del primer fallo, para acompañar a la explicación pedagógica. */
  detalle?: string;
}

const OK: CheckResult = { correct: true };

/** Secuencia de ids de pieza tal y como la colocó el jugador. */
export type OrderingResponse = string[];
/** Índice del elemento de la izquierda → índice del elemento de la derecha. */
export type MatchingResponse = Record<number, number>;
/** id de pieza → id de cesta. */
export type SortingResponse = Record<string, string>;
/** ids de las piezas seleccionadas. */
export type BuildingResponse = string[];
/** Palabras que el jugador ya encontró en la sopa de letras. */
export type WordsearchResponse = string[];

export function checkOrdering(
  piezas: { id: string; label: string }[],
  orden: string[],
  response: OrderingResponse
): CheckResult {
  if (response.length !== orden.length) {
    return { correct: false, detalle: `Faltan ${orden.length - response.length} pieza(s) por colocar.` };
  }
  for (let i = 0; i < orden.length; i++) {
    if (response[i] !== orden[i]) {
      const esperada = piezas.find((p) => p.id === orden[i]);
      return {
        correct: false,
        detalle: `En la posición ${i + 1} va ${esperada ? `«${esperada.label}»` : orden[i]}.`
      };
    }
  }
  return OK;
}

export function checkMatching(
  pares: { izquierda: string; derecha: string }[],
  response: MatchingResponse
): CheckResult {
  const pendientes: number[] = [];
  const mal: number[] = [];
  pares.forEach((_, index) => {
    if (response[index] === undefined) pendientes.push(index);
    else if (response[index] !== index) mal.push(index);
  });
  if (pendientes.length > 0) {
    return { correct: false, detalle: `Quedan ${pendientes.length} elemento(s) sin unir.` };
  }
  if (mal.length > 0) {
    const primero = mal[0];
    return {
      correct: false,
      detalle: `«${pares[primero].izquierda}» no va con «${pares[response[primero]].derecha}».`
    };
  }
  return OK;
}

export function checkSorting(
  piezas: { id: string; label: string; bucket: string }[],
  cestas: { id: string; label: string }[],
  response: SortingResponse
): CheckResult {
  const pendientes = piezas.filter((p) => response[p.id] === undefined);
  if (pendientes.length > 0) {
    return { correct: false, detalle: `Quedan ${pendientes.length} ficha(s) sin clasificar.` };
  }
  const mal = piezas.filter((p) => response[p.id] !== p.bucket);
  if (mal.length > 0) {
    const cesta = cestas.find((c) => c.id === mal[0].bucket);
    return {
      correct: false,
      detalle: `«${mal[0].label}» pertenece a ${cesta ? `«${cesta.label}»` : 'otra cesta'}.`
    };
  }
  return OK;
}

export function checkBuilding(
  piezas: { id: string; value: number }[],
  objetivo: number,
  solucion: string[],
  response: BuildingResponse
): CheckResult {
  const seleccion = new Set(response);
  const esperada = new Set(solucion);
  if (seleccion.size === esperada.size && [...esperada].every((id) => seleccion.has(id))) return OK;

  const suma = piezas
    .filter((p) => seleccion.has(p.id))
    .reduce((total, p) => total + p.value, 0);
  return { correct: false, detalle: `Eso suma ${suma} y necesitas ${objetivo}.` };
}

export function checkChoice(correcta: number, index: number): CheckResult {
  return index === correcta ? OK : { correct: false };
}

export function checkNumeric(respuesta: number, value: number): CheckResult {
  return value === respuesta ? OK : { correct: false };
}

/** Normaliza texto para comparar sin penalizar mayúsculas, acentos ni espacios. */
export function normalizeText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Comprueba respuestas de texto: cada blanco acepta varias soluciones válidas. */
export function checkText(blancos: string[][], values: string[]): CheckResult {
  if (values.length !== blancos.length) {
    return { correct: false, detalle: `Faltan ${blancos.length - values.length} respuesta(s).` };
  }
  for (let i = 0; i < blancos.length; i++) {
    const value = normalizeText(values[i] ?? '');
    if (value === '') {
      return { correct: false, detalle: `El espacio ${i + 1} está vacío.` };
    }
    if (!blancos[i].some((aceptada) => normalizeText(aceptada) === value)) {
      return { correct: false, detalle: `«${values[i]}» no es correcto en el espacio ${i + 1}.` };
    }
  }
  return OK;
}

/** Comprueba la sopa de letras: hay que hallar todas las palabras de la lista. */
export function checkWordsearch(palabras: string[], response: WordsearchResponse): CheckResult {
  const halladas = new Set(response.map(normalizeText));
  const faltan = palabras.filter((p) => !halladas.has(normalizeText(p)));
  if (faltan.length === 0) return OK;
  return {
    correct: false,
    detalle: `Queda${faltan.length === 1 ? '' : 'n'} ${faltan.length} palabra(s) por encontrar, como «${faltan[0]}».`
  };
}
